import { useState, useMemo, memo } from "react";
import { fmt, formatOptionName, formatExpiry } from "./utils";

// Map underlying name → slug for option contract page routing
const UNDERLYING_SLUG = {
  "NIFTY": "nifty", "BANKNIFTY": "banknifty", "FINNIFTY": "finnifty",
  "MIDCPNIFTY": "midcpnifty", "SENSEX": "sensex",
};

const TopOptionsSidebar = memo(({ indexName, indexShortName, options, optionTicks, oiSummary, kiteAuthenticated, navigate }) => {
  const [sidebarFilter, setSidebarFilter] = useState("all");

  // Dynamic re-ranking: merge WS ticks with initial data, re-sort by live OI
  const enrichedOptions = useMemo(() => {
    return options.map(opt => {
      const tick = optionTicks[opt.tradingsymbol] || {};
      return {
        ...opt,
        _liveOi: tick.oi || opt.oi || 0,
        _liveVol: tick.volume || opt.volume || 0,
        _livePrice: tick.last_price || opt.last_price || 0,
        _liveChgPct: tick.change_percent ?? opt.change_percent ?? 0,
      };
    });
  }, [options, optionTicks]);

  // Re-rank by live OI descending (Issue #8: dynamic re-ranking)
  const rankedOptions = useMemo(() => {
    const sorted = [...enrichedOptions];
    if (sorted.some(o => o._liveOi > 0)) {
      sorted.sort((a, b) => b._liveOi - a._liveOi);
    }
    return sorted;
  }, [enrichedOptions]);

  const filteredSidebarOptions = useMemo(() => {
    if (sidebarFilter === "all") return rankedOptions;
    return rankedOptions.filter(o => sidebarFilter === "call" ? o.type === "Call" : o.type === "Put");
  }, [rankedOptions, sidebarFilter]);

  // Show options whenever we have any in the response (Fix #2: no gating on OI)
  const hasOptions = options.length > 0;
  const showUnavailable = !hasOptions && oiSummary?.unavailable;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 sticky top-20 max-h-[calc(100vh-6rem)] flex flex-col" data-testid="top-options-sidebar">
      <h3 className="text-base font-bold text-gray-900 mb-3">Top {indexName} Options</h3>
      {/* Filter tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 mb-3">
        {["all", "put", "call"].map(f => (
          <button key={f} onClick={() => setSidebarFilter(f)}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors flex-1 ${sidebarFilter === f ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            data-testid={`sidebar-filter-${f}`}
          >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
      </div>
      {showUnavailable ? (
        <p className="text-sm text-gray-400 text-center py-4" data-testid="sidebar-unavailable">
          {!kiteAuthenticated ? "OI data temporarily unavailable" : "No options data available"}
        </p>
      ) : filteredSidebarOptions.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">No options data available</p>
      ) : (
        <div className="space-y-0 overflow-y-auto flex-1">
          {filteredSidebarOptions.map((opt) => {
            const price = opt._livePrice;
            const oi = opt._liveOi;
            const vol = opt._liveVol;
            const chgPct = opt._liveChgPct;
            const isPos = chgPct >= 0;
            const humanName = formatOptionName(indexShortName, opt.strike, opt.type);
            // Route to dedicated option contract page (Fix #3)
            const underlyingSlug = UNDERLYING_SLUG[opt.name || indexShortName] || (opt.name || indexShortName).toLowerCase();
            return (
              <div
                key={opt.tradingsymbol}
                className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer rounded px-1 transition-colors"
                onClick={() => navigate(`/options/${underlyingSlug}/${opt.tradingsymbol}?exchange=${opt.exchange || 'NFO'}`)}
                data-testid={`sidebar-option-${opt.tradingsymbol}`}
              >
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${opt.type === "Call" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{opt.type === "Call" ? "CE" : "PE"}</span>
                    <span className="text-sm text-gray-800">{humanName}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    OI: {oi > 0 ? oi.toLocaleString("en-IN") : (oi === 0 ? "0" : "--")} | Vol: {vol > 0 ? vol.toLocaleString("en-IN") : (vol === 0 ? "0" : "--")}
                    {opt.expiry && <span className="ml-1">| {formatExpiry(opt.expiry)}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{price ? `₹${fmt(price)}` : "--"}</p>
                  {chgPct !== 0 ? (
                    <p className={`text-xs ${isPos ? "text-[#00b386]" : "text-[#eb5b3c]"}`}>
                      {isPos ? "+" : ""}{chgPct.toFixed(2)}%
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">--</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default TopOptionsSidebar;
